<template>
    <DialogRoot class="overflow-hidden">
        <DialogTrigger :as-child="asChild">
            <slot name="trigger"></slot>
        </DialogTrigger>
        <DialogPortal :disabled="disablePortal">
            <DialogOverlay :class="[classes.overlay, overlayClass]" />
            <DialogContent :class="[classes.content, dialogContentClass]" @interact-outside="handleInteractOutside" @escape-key-down="handleInteractOutside">
                <VisuallyHidden as-child>
                    <DialogTitle>{{ title }}</DialogTitle>
                </VisuallyHidden>
                <VisuallyHidden as-child>
                    <DialogDescription v-if="description">{{ description }}</DialogDescription>
                </VisuallyHidden>
                <div v-if="showClose" :class="floatClose ? 'absolute top-0 right-0 z-10' : 'w-full text-right'">
                    <DialogClose aria-label="Close dialog" class="p-4 bi bi-x-lg text-base/none icon-semibold dialogClose"></DialogClose>
                </div>
                <slot></slot>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle, DialogTrigger, VisuallyHidden } from "reka-ui";

interface Props {
    asChild?: boolean;
    disablePortal?: boolean;
    disableCloseOnBackdropClick?: boolean;
    title: string;
    description?: string;
    showClose?: boolean;
    // When true (and showClose is set), the close button is absolutely positioned in the
    // top-right corner instead of occupying its own row above the content. Use this when the
    // dialog body already has top padding, to avoid stacked whitespace above the content.
    floatClose?: boolean;
    dialogContentClass?: string;
    overlayClass?: string;
    onInteractOutside?: () => void | null;
}

const props = withDefaults(defineProps<Props>(), {
    asChild: true,
    disablePortal: false,
    disableCloseOnBackdropClick: false,
    description: "",
    showClose: false,
    floatClose: false,
    overlayClass: "",
});

const handleInteractOutside = (event: Event) => {
    if (props.disableCloseOnBackdropClick) {
        event.preventDefault();
        return;
    }
    // Prevent closing when the interaction targets a teleported element opted out of dismissal
    const originalTarget = (event as CustomEvent)?.detail?.originalEvent?.target as Element | null;
    if (originalTarget?.closest("[data-dismiss-ignore]")) {
        event.preventDefault();
        return;
    }
    if (props.onInteractOutside) {
        props.onInteractOutside();
    }
};
</script>

<style module="classes">
@reference "../../../css/tailwind.css";

@layer components {
    /* Colours come from css/tokens/dialog.css so the tonal flag can restyle
       the scrim and surface without a selector override here. */
    .overlay {
        @apply bg-dialog-overlay/40 w-dvw h-dvh absolute top-0 left-0 z-30;
    }

    .content {
        @apply max-w-full md:max-w-max w-full m-0 bottom-0 md:bottom-auto absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 focus-visible:outline-0 bg-dialog-surface rounded-b-none md:rounded-b-xl rounded-xl overflow-y-auto max-h-[75dvh] md:max-h-[90dvh] z-30 pb-(--mobile-nav-total-height) md:pb-0;
    }
}
</style>
